import React, { useState } from "react";
import {
	Box,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import CommCard from "src/common/CommCard";
import { CommonPagination } from "src/common/CommonPagination";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import GiftTableRow from "./GiftTableRow";
import UpdateGiftModal from "./UpdateGiftModal";
import DeleteGiftModal from "./DeleteGiftModal";
import type { IGiftItem, TGiftStatusFilter } from "./type";

const GiftManagementPage: React.FC = () => {
	const [pagination, setPagination] = useState({ limit: 20, skip: 0 });
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [statusFilter, setStatusFilter] = useState<TGiftStatusFilter>("");

	const [editModalState, setEditModalState] = useState<{
		open: boolean;
		gift: IGiftItem | null;
	}>({
		open: false,
		gift: null,
	});

	const [deleteModalState, setDeleteModalState] = useState<{
		open: boolean;
		gift: IGiftItem | null;
	}>({
		open: false,
		gift: null,
	});

	// Fetch Gifts from /api/v1/admin/gift with filters
	const { data, isLoading } = useGetQueryWithTotal<IGiftItem[]>({
		endPoint: "/api/v1/admin/gift",
		queryKeys: [
			"Gift",
			statusFilter,
			pagination.skip,
			pagination.limit,
		],
		params: {
			limit: pagination.limit,
			skip: pagination.skip,
			...(statusFilter !== "" && statusFilter !== "all"
				? { gift_collected: Number(statusFilter) }
				: {}),
			...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
		},
	});

	const giftList: IGiftItem[] = Array.isArray(data)
		? data
		: Array.isArray(data?.data)
			? data.data
			: Array.isArray((data as any)?.data?.data)
				? (data as any).data.data
				: [];

	const totalItems = data?.total ?? giftList.length;

	const handleOpenEdit = (gift: IGiftItem) => {
		setEditModalState({ open: true, gift });
	};

	const handleCloseEdit = () => {
		setEditModalState((prev) => ({ ...prev, open: false }));
	};

	const handleOpenDelete = (gift: IGiftItem) => {
		setDeleteModalState({ open: true, gift });
	};

	const handleCloseDelete = () => {
		setDeleteModalState((prev) => ({ ...prev, open: false }));
	};

	return (
		<>
			<CommCard
				showCardHeader
				headerTitle={`Gift List (${totalItems})`}
				helmetTitle="BTTF - Gift Management"
				searchPlaceHolder="Search visitor, phone, pass no..."
				handleSearchFun={(val) => {
					setSearchQuery(val);
					setPagination((prev) => ({ ...prev, skip: 0 }));
				}}
				showStatus
				statusLabel="Status"
				statusOptions={[
					{ id: "all", name: "All Gifts" },
					{ id: "1", name: "Collected" },
					{ id: "0", name: "Not Collected" },
				]}
				handleSelectStatus={(val) => {
					setStatusFilter(val === "all" ? "" : (val as TGiftStatusFilter));
					setPagination((prev) => ({ ...prev, skip: 0 }));
				}}
			>
				{/* Table */}
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 700 }}>SL.</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>Visitor Pass No</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>Visitor Name</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
								<TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={8} align="center" sx={{ py: 4 }}>
										<CircularProgress size={32} />
									</TableCell>
								</TableRow>
							) : giftList.length > 0 ? (
								giftList.map((gift, idx) => (
									<GiftTableRow
										key={`gift-${gift.id ?? idx}`}
										gift={gift}
										serial={idx + 1 + pagination.skip}
										onEdit={handleOpenEdit}
										onDelete={handleOpenDelete}
									/>
								))
							) : (
								<TableRow>
									<TableCell colSpan={8} align="center" sx={{ py: 4 }}>
										<Typography variant="h5" fontWeight={600} color="text.secondary">
											Not Found
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				{/* Pagination */}
				<Box p={2}>
					<CommonPagination
						total={totalItems}
						paginationCalFun={(limit: number, skip: number) => {
							setPagination({ limit, skip });
						}}
					/>
				</Box>
			</CommCard>

			{/* Update Gift Status Modal */}
			<UpdateGiftModal
				open={editModalState.open}
				handleClose={handleCloseEdit}
				gift={editModalState.gift}
			/>

			{/* Delete Gift Confirmation Modal */}
			<DeleteGiftModal
				open={deleteModalState.open}
				handleClose={handleCloseDelete}
				gift={deleteModalState.gift}
			/>
		</>
	);
};

export default GiftManagementPage;
